import React, { useCallback, useState } from 'react';
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEllipsisVertical,
    faCaretDown,
    faCaretRight,
} from '@fortawesome/free-solid-svg-icons';
import {
    faPenToSquare,
    faTrash,
    faDownload,
} from '@fortawesome/pro-regular-svg-icons';
import { formatDateLong, formatFileSize, DATE_FORMAT_OPTIONS_WITH_TIME } from 'ns_libs/formatter';
import './DocumentLibraryTable.scss';
import { DocumentLibraryColumn } from 'views/DocumentLibrary/constants';
import classNames from 'classnames';
import { DealDirectoryItem } from '../../types';
import { checkIfIsFile, getFileIcon } from '../../helpers';

interface FileRowProps {
    item: DealDirectoryItem;
    visibleColumns: Record<string, boolean>;
    showAllColumns: boolean;
    level: number;
    expanded: { [key: string]: boolean };
    onToggle: (id: number) => void;
    onSelect: (id: number) => void;
    onDocumentEdit: (id: number) => void;
    onEdit: (item: DealDirectoryItem) => void;
    onDelete: (item: DealDirectoryItem) => void;
}

const FileRow = ({
    item, visibleColumns, showAllColumns, level, expanded, onToggle, onSelect, onDelete, onEdit, onDocumentEdit,
}: FileRowProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const isFile = checkIfIsFile(item);
    const isFolder = !isFile;
    const isExpanded = Boolean(isFolder && item.directoryId && expanded[item.directoryId]);
    const hasChildren = isFolder && (item?.children.length > 0 || item?.documents.length > 0);
    const baseClass = 'DocumentLibraryTable__indent';
    const levelClass = `${baseClass}--level-${level}`;
    const baseIndentClass = `${baseClass} ${levelClass}`;
    const fileModifier = 'DocumentLibraryTable__indent--file';
    const fileClass = isFile ? ` ${fileModifier}` : '';
    const indentClass = `${baseIndentClass}${fileClass}`;

    const handleRowClick = (e: React.MouseEvent) => {
        if (isFolder) {
            if (item.directoryId && hasChildren) onToggle(item.directoryId);
        } else {
            onSelect(item.id);
        }
    };

    const handleDownload = useCallback(() => {
        if ('s3Url' in item && item.s3Url) window.open(item.s3Url, '_blank');
    }, [item]);

    const preventClickPropagation = (e: React.MouseEvent, callback: CallableFunction) => {
        e.stopPropagation();
        callback();
    };

    return (
        <>
            <tr
                className={classNames(
                    'NSTable__tbody__tr DocumentLibraryTable__row',
                    { 'cursor--pointer': isFile || hasChildren, 'heap-deal-documents-click-document': isFile },
                )}
                onClick={handleRowClick}
                role="button"
                aria-label={isFolder ? `${isExpanded ? 'Collapse' : 'Expand'} folder ${item.directoryName}` : `Open file ${item.name}`}
            >
                {(!showAllColumns && !visibleColumns[DocumentLibraryColumn.NAME])
                    && <td className="NSTable__tbody__tr__td text-center DocumentLibraryTable__actions-column" />}
                {(showAllColumns || visibleColumns[DocumentLibraryColumn.NAME]) && (
                    <td className="NSTable__tbody__tr__td DocumentLibraryTable__cell--limited-width">
                        <div className={`text-truncate ${indentClass}`}>
                            {hasChildren && (
                                <span className={
                                    classNames(
                                        'DocumentLibraryTable__toggle-btn',
                                        { 'text-muted': isExpanded && hasChildren },
                                    )
                                }
                                >
                                    <FontAwesomeIcon icon={isExpanded ? faCaretDown : faCaretRight} />
                                </span>
                            )}
                            <FontAwesomeIcon
                                icon={getFileIcon({ fileFormat: isFile ? item.extension : undefined, isExpanded })}
                                className={
                                    classNames(
                                        'text-white mr-2',
                                        { 'text-muted': isExpanded && isFolder && hasChildren, 'ml-2': isFolder && !hasChildren },
                                    )
                                }
                            />
                            <span
                                className={classNames({
                                    'text-muted': isExpanded && isFolder && hasChildren,
                                    'text-white': !isExpanded || !isFolder || !hasChildren,
                                })}
                            >
                                {isFile ? item.name : item.directoryName}
                            </span>
                        </div>
                    </td>
                )}
                {(showAllColumns || visibleColumns[DocumentLibraryColumn.TYPE]) && (
                    <td className="NSTable__tbody__tr__td">
                        {isFile && (
                            <div className={
                                classNames(
                                    'text-white',
                                    { 'DocumentLibraryTable__document-type': item.type },
                                )
                            }
                            >
                                {item.type || '-'}
                            </div>
                        )}
                    </td>
                )}
                {(showAllColumns || visibleColumns[DocumentLibraryColumn.FORMAT]) && (
                    <td className="NSTable__tbody__tr__td">
                        <div className="text-white">{isFile ? item.extension || '' : ''}</div>
                    </td>
                )}
                {(showAllColumns || visibleColumns[DocumentLibraryColumn.DATE_MODIFIED]) && (
                    <td className="NSTable__tbody__tr__td">
                        <div className="text-white">{isFile ? formatDateLong(item.updatedAt, DATE_FORMAT_OPTIONS_WITH_TIME) || '' : ''}</div>
                    </td>
                )}
                {(showAllColumns || visibleColumns[DocumentLibraryColumn.SIZE]) && (
                    <td className="NSTable__tbody__tr__td">
                        <div className="text-white">{isFile ? formatFileSize(item.size) || '' : ''}</div>
                    </td>
                )}
                <td className="NSTable__tbody__tr__td text-center DocumentLibraryTable__actions-column">
                    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} direction="down">
                        <DropdownToggle
                            tag="div"
                            className="cursor--pointer DocumentLibraryTable__options-btn"
                            onClick={e => preventClickPropagation(e, () => setIsOpen(!isOpen))}
                        >
                            <FontAwesomeIcon data-testid="ellipsis-icon" icon={faEllipsisVertical} />
                        </DropdownToggle>
                        <DropdownMenu right>
                            {isFolder ? (
                                <>
                                    <DropdownItem
                                        onClick={e => preventClickPropagation(e, () => onEdit(item))}
                                        data-testid="rename-folder-action"
                                        className="text-white DocumentLibraryTable__dropdown-item"
                                    >
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <span>Rename folder</span>
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </div>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={e => preventClickPropagation(e, () => onDelete(item))}
                                        data-testid="delete-folder-action"
                                        className="text-white DocumentLibraryTable__dropdown-item"
                                    >
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <span>Delete folder</span>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </div>
                                    </DropdownItem>
                                </>
                            ) : (
                                <>
                                    <DropdownItem
                                        onClick={e => preventClickPropagation(e, () => handleDownload())}
                                        data-testid="download-action"
                                        className="text-white DocumentLibraryTable__dropdown-item"
                                    >
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <span>Download</span>
                                            <FontAwesomeIcon icon={faDownload} />
                                        </div>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={e => preventClickPropagation(e, () => onDocumentEdit(item.id))}
                                        data-testid="edit-action"
                                        className="text-white DocumentLibraryTable__dropdown-item"
                                    >
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <span>Edit</span>
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </div>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={e => preventClickPropagation(e, () => onDelete(item))}
                                        data-testid="delete-action"
                                        className="text-white DocumentLibraryTable__dropdown-item"
                                    >
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <span>Delete</span>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </div>
                                    </DropdownItem>
                                </>
                            )}
                        </DropdownMenu>
                    </Dropdown>
                </td>
            </tr>
            {isExpanded && isFolder && item.children?.map(child => (
                <FileRow
                    key={`dir-${child.directoryId}`}
                    item={child}
                    showAllColumns={showAllColumns}
                    visibleColumns={visibleColumns}
                    level={level + 1}
                    expanded={expanded}
                    onToggle={onToggle}
                    onSelect={onSelect}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDocumentEdit={onDocumentEdit}
                />
            ))}
            {isExpanded && isFolder && item.documents?.map(child => (
                <FileRow
                    key={`file-${child.id}`}
                    item={child}
                    showAllColumns={showAllColumns}
                    visibleColumns={visibleColumns}
                    level={level + 1}
                    expanded={expanded}
                    onToggle={onToggle}
                    onSelect={onSelect}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDocumentEdit={onDocumentEdit}
                />
            ))}
        </>
    );
};

export default FileRow;
